import Papa from 'papaparse'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

// 扩展jsPDF类型以包含autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF
    lastAutoTable: {
      finalY: number
    }
  }
}
import { ManualTestTrade, ManualTestStats } from '../types'

// CSV导出功能
export const exportToCSV = (
  trades: ManualTestTrade[],
  strategyName: string,
  dateFormat: 'ISO' | 'custom' = 'ISO'
) => {
  // 准备CSV数据
  const csvData = trades.map(trade => ({
    '入场时间': dateFormat === 'ISO' 
      ? new Date(trade.entry_time).toISOString()
      : new Date(trade.entry_time).toLocaleString('zh-CN'),
    '出场时间': dateFormat === 'ISO'
      ? new Date(trade.exit_time).toISOString()
      : new Date(trade.exit_time).toLocaleString('zh-CN'),
    '交易方向': trade.direction === 'long' ? '多头' : '空头',
    '入场价格': trade.entry_price,
    '出场价格': trade.exit_price,
    '仓位大小': trade.size,
    '盈亏金额': trade.pnl_amount,
    'R值': trade.pnl_r,
    '入场理由': trade.reason_entry,
    '出场理由': trade.reason_exit,
    '主观干预': trade.manual_override ? '是' : '否',
    '备注': trade.notes || '',
    '创建时间': new Date(trade.created_at).toLocaleString('zh-CN')
  }))

  // 生成CSV
  const csv = Papa.unparse(csvData)
  
  // 创建Blob
  const blob = new Blob(['\ufeff' + csv], { 
    type: 'text/csv;charset=utf-8;' 
  })
  
  // 生成文件名
  const date = new Date().toISOString().split('T')[0]
  const fileName = `manual_trades_${strategyName}_${date}.csv`
  
  // 下载文件
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = fileName
  link.click()
  URL.revokeObjectURL(link.href)
}

// PDF导出功能
export const exportToPDF = (
  trades: ManualTestTrade[],
  stats: ManualTestStats,
  strategyName: string,
  language: 'zh' | 'en' = 'zh'
) => {
  const doc = new jsPDF()
  
  // 设置中文字体支持
  doc.addFont('https://cdn.jsdelivr.net/npm/noto-sans-sc@1.0.1/NotoSansSC-Regular.otf', 'NotoSansSC', 'normal')
  doc.setFont('NotoSansSC')
  
  const t = language === 'zh' ? {
    title: '手动测试报告',
    strategyName: '策略名称',
    testPeriod: '测试期间',
    totalTrades: '总交易笔数',
    winRate: '胜率',
    avgRValue: '平均R值',
    maxDrawdown: '最大回撤',
    totalPnL: '总盈亏',
    tradeDetails: '交易明细',
    notes: '备注',
    generatedBy: '生成人',
    generatedAt: '生成时间',
    entryTime: '入场时间',
    exitTime: '出场时间',
    direction: '方向',
    entryPrice: '入场价格',
    exitPrice: '出场价格',
    size: '仓位',
    pnl: '盈亏',
    rValue: 'R值',
    manualOverride: '干预',
    long: '多头',
    short: '空头',
    yes: '是',
    no: '否'
  } : {
    title: 'Manual Test Report',
    strategyName: 'Strategy Name',
    testPeriod: 'Test Period',
    totalTrades: 'Total Trades',
    winRate: 'Win Rate',
    avgRValue: 'Avg R Value',
    maxDrawdown: 'Max Drawdown',
    totalPnL: 'Total PnL',
    tradeDetails: 'Trade Details',
    notes: 'Notes',
    generatedBy: 'Generated By',
    generatedAt: 'Generated At',
    entryTime: 'Entry Time',
    exitTime: 'Exit Time',
    direction: 'Direction',
    entryPrice: 'Entry Price',
    exitPrice: 'Exit Price',
    size: 'Size',
    pnl: 'PnL',
    rValue: 'R Value',
    manualOverride: 'Override',
    long: 'Long',
    short: 'Short',
    yes: 'Yes',
    no: 'No'
  }

  let yPosition = 20

  // 封面标题
  doc.setFontSize(24)
  doc.text(t.title, 105, yPosition, { align: 'center' })
  yPosition += 20

  // 策略信息
  doc.setFontSize(12)
  doc.text(`${t.strategyName}: ${strategyName}`, 20, yPosition)
  yPosition += 10

  // 统计信息
  const startDate = trades.length > 0 ? new Date(trades[0].entry_time).toLocaleDateString() : '-'
  const endDate = trades.length > 0 ? new Date(trades[trades.length - 1].exit_time).toLocaleDateString() : '-'
  doc.text(`${t.testPeriod}: ${startDate} - ${endDate}`, 20, yPosition)
  yPosition += 10

  doc.text(`${t.totalTrades}: ${stats.total_trades}`, 20, yPosition)
  yPosition += 10

  doc.text(`${t.winRate}: ${(stats.win_rate * 100).toFixed(1)}%`, 20, yPosition)
  yPosition += 10

  doc.text(`${t.avgRValue}: ${stats.avg_r_value.toFixed(2)}`, 20, yPosition)
  yPosition += 10

  doc.text(`${t.maxDrawdown}: ${stats.max_drawdown.toFixed(2)}`, 20, yPosition)
  yPosition += 10

  doc.text(`${t.totalPnL}: ${stats.total_pnl >= 0 ? '+' : ''}${stats.total_pnl.toFixed(2)}`, 20, yPosition)
  yPosition += 20

  // 交易明细表格
  if (trades.length > 0) {
    doc.setFontSize(14)
    doc.text(t.tradeDetails, 20, yPosition)
    yPosition += 10

    // 准备表格数据
    const tableData = trades.map(trade => [
      new Date(trade.entry_time).toLocaleDateString(),
      trade.direction === 'long' ? t.long : t.short,
      trade.entry_price.toFixed(2),
      trade.exit_price.toFixed(2),
      trade.size.toFixed(2),
      `${trade.pnl_amount >= 0 ? '+' : ''}${trade.pnl_amount.toFixed(2)}`,
      `${trade.pnl_r >= 0 ? '+' : ''}${trade.pnl_r.toFixed(2)}`,
      trade.manual_override ? t.yes : t.no
    ])

    // 添加表格
    doc.autoTable({
      startY: yPosition,
      head: [[
        t.entryTime,
        t.direction,
        t.entryPrice,
        t.exitPrice,
        t.size,
        t.pnl,
        t.rValue,
        t.manualOverride
      ]],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 15 },
        2: { cellWidth: 20 },
        3: { cellWidth: 20 },
        4: { cellWidth: 20 },
        5: { cellWidth: 20 },
        6: { cellWidth: 20 },
        7: { cellWidth: 15 }
      }
    })

    yPosition = (doc as any).lastAutoTable.finalY + 10
  }

  // 页脚信息
  doc.setFontSize(10)
  doc.text(`${t.generatedAt}: ${new Date().toLocaleString()}`, 20, yPosition)

  // 保存PDF
  const fileName = `manual_test_report_${strategyName}_${new Date().toISOString().split('T')[0]}.pdf`
  doc.save(fileName)
}

// 自动计算盈亏
export const calculatePnL = (
  entryPrice: number,
  exitPrice: number,
  size: number,
  direction: 'long' | 'short'
): number => {
  if (direction === 'long') {
    return (exitPrice - entryPrice) * size
  } else {
    return (entryPrice - exitPrice) * size
  }
}

// 验证时间顺序
export const validateTimeOrder = (entryTime: string, exitTime: string): boolean => {
  return new Date(entryTime) < new Date(exitTime)
} 